import {
  getBlocksuiteLocaleKey,
  LocaleProvider,
  type ToolbarAction,
  translateBlocksuiteTextWithTextFallback,
  ToolbarContext,
} from '@blocksuite/affine-shared/services';
import { SignalWatcher } from '@blocksuite/global/lit';
import { PropTypes, requiredProperties } from '@blocksuite/std';
import type { ReadonlySignal, Signal } from '@preact/signals-core';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { html } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { repeat } from 'lit-html/directives/repeat.js';

import { EditorChevronDown } from '../toolbar';

@requiredProperties({
  actions: PropTypes.array,
  context: PropTypes.instanceOf(ToolbarContext),
  viewTypeSignal: PropTypes.object,
})
export class ViewDropdownMenu extends SignalWatcher(LitElement) {
  @property({ attribute: false })
  accessor actions!: ToolbarAction[];

  @property({ attribute: false })
  accessor context!: ToolbarContext;

  @property({ attribute: false })
  accessor viewTypeSignal!: Signal<string> | ReadonlySignal<string>;

  private _translate(
    context: ToolbarContext,
    kind: 'label' | 'tooltip',
    value: string
  ) {
    return translateBlocksuiteTextWithTextFallback(
      context.std.getOptional(LocaleProvider),
      getBlocksuiteLocaleKey(`toolbar.${kind}`, value),
      value,
      `toolbar.${kind}`
    );
  }

  override render() {
    const {
      actions,
      context,
      viewTypeSignal: { value: viewType },
    } = this;
    const translatedViewType = this._translate(context, 'label', viewType);

    return html`
      <editor-menu-button
        .contentPadding="${'8px'}"
        .button=${html`
          <editor-icon-button
            aria-label=${this._translate(context, 'label', 'Switch view')}
            .tooltip=${this._translate(context, 'tooltip', 'Switch view')}
            .justify="${'space-between'}"
            .labelHeight="${'20px'}"
            .iconContainerWidth="${'110px'}"
          >
            <span class="label">${translatedViewType}</span>
            ${EditorChevronDown}
          </editor-icon-button>
        `}
      >
        <div data-size="small" data-orientation="vertical">
          ${repeat(
            actions.filter(action => {
              if (typeof action.when === 'function')
                return action.when(context);
              return action.when ?? true;
            }),
            action => action.id,
            ({ id, label, disabled, run }) => {
              const translatedLabel = this._translate(
                context,
                'label',
                label ?? id
              );
              return html`
                <editor-menu-action
                  aria-label="${ifDefined(translatedLabel)}"
                  data-testid="${`link-to-${id}`}"
                  ?data-selected="${label === viewType}"
                  ?disabled="${ifDefined(
                    typeof disabled === 'function'
                      ? disabled(context)
                      : disabled
                  )}"
                  @click=${() => run?.(context)}
                >
                  ${translatedLabel}
                </editor-menu-action>
              `;
            }
          )}
        </div>
      </editor-menu-button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-view-dropdown-menu': ViewDropdownMenu;
  }
}
